<template>
  <UButton
    :size
    v-bind="variantStyle"
    :loading="isLoading"
    :disabled="isDisabled"
  >
    <slot />
  </UButton>
</template>

<script setup lang="ts">
import { Size, Variant } from '@/types/enums'

const props = withDefaults(
  defineProps<{
    isDisabled?: boolean
    isLoading?: boolean
    size?: Size
    variant?: Variant
  }>(),
  {
    size: Size.md,
    variant: Variant.primary,
  }
)

const variantStyle = computed<{
  color?: 'primary' | 'neutral' | 'success' | 'secondary'
  variant?: 'link' | 'solid' | 'outline' | 'soft' | 'subtle' | 'ghost'
  ui?: { base: string }
}>(() => {
  switch (props.variant) {
    case Variant.secondary:
      return {
        variant: 'solid',
        color: 'secondary',
        ui: {
          base: 'text-default',
        },
      }

    case Variant.secondaryDark:
      return {
        variant: 'solid',
        color: 'neutral',
        ui: {
          base: 'text-default bg-muted hover:bg-muted/60',
        },
      }

    case Variant.tertiary:
      return {
        variant: 'outline',
      }

    case Variant.success:
      return {
        color: 'success',
        variant: 'solid',
      }

    case Variant.link:
      return {
        variant: 'link',
        ui: {
          base: 'underline decoration-1 underline-offset-3',
        },
      }

    case Variant.secondaryLink:
      return {
        variant: 'link',
        color: 'neutral',
        ui: {
          base: 'underline decoration-primary decoration-1 underline-offset-3',
        },
      }

    case Variant.tertiaryLink:
      return {
        variant: 'link',
        color: 'neutral',
        ui: {
          base: 'font-light underline',
        },
      }

    case Variant.quaternaryLink:
      return {
        variant: 'link',
        ui: {
          base: 'text-cpurple font-light underline hover:text-cpurple/75',
        },
      }

    case Variant.primary:
    default:
      return {
        color: 'primary',
        variant: 'solid',
      }
  }
})
</script>
